import type { Scope } from "@/lib/connection"
import type { Decision } from "./data"

/** A verdict sent to the cell whose outcome is not confirmed yet. */
export interface PendingVerdict {
  readonly outcome: "accepted" | "rejected"
  /** The frozen request: a retry resends it byte for byte under its original idempotency key. */
  readonly body: string
  /** The decision record's version when it was sent (0: none yet). */
  readonly baseVersion: number
}

/**
 * Unconfirmed verdicts per cell and scope, kept in this tab's session storage so the guard against
 * reversing an unknown outcome survives the review cell unmounting and the page reloading.
 */
export class PendingVerdicts {
  private readonly storageKey: string

  constructor(
    baseUrl: string,
    scope: Scope,
    private readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = typeof sessionStorage === "undefined" ? null : sessionStorage,
  ) {
    this.storageKey = `norn.workbook.pending:${baseUrl}:${scope.orgId}/${scope.environmentId}`
  }

  private read(): Record<string, PendingVerdict> {
    try {
      const parsed: unknown = JSON.parse(this.storage?.getItem(this.storageKey) ?? "{}")
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, PendingVerdict>) : {}
    } catch {
      return {}
    }
  }

  private write(all: Record<string, PendingVerdict>) {
    if (Object.keys(all).length) this.storage?.setItem(this.storageKey, JSON.stringify(all))
    else this.storage?.removeItem(this.storageKey)
  }

  get(key: string): PendingVerdict | undefined {
    return this.read()[key]
  }

  set(key: string, verdict: PendingVerdict) {
    this.write({ ...this.read(), [key]: verdict })
  }

  /** Retire the entry, but only if it is still this outcome (a newer submission keeps its own). */
  retire(key: string, outcome: PendingVerdict["outcome"]) {
    const all = this.read()
    if (all[key]?.outcome !== outcome) return
    this.write(Object.fromEntries(Object.entries(all).filter(([k]) => k !== key)))
  }

  /**
   * The open verdict on this decision, after reconciling with the ledger: a decision record written
   * since it was sent, with its outcome, proves it committed, so it no longer blocks the other one.
   */
  open(key: string, recorded: Decision | undefined): PendingVerdict | undefined {
    const verdict = this.get(key)
    if (verdict && recorded && recorded.version > verdict.baseVersion && recorded.outcome === verdict.outcome) {
      this.retire(key, verdict.outcome)
      return undefined
    }
    return verdict
  }
}
